// Explicit Sentry reporting for Reveal BI client failures.
//
// The SDK swallows its failures into callbacks, so nothing reaches window.onerror,
// and the shapes that do escape ("Failed to fetch", "Load failed", "Script error.")
// are all on the global ignoreErrors list in layouts/_sentry.html.haml. That list is
// applied by InboundFilters to manually captured events too, and it runs before
// beforeSend — so a rescue hook can't undo it. We therefore report a synthetic Error
// whose message can never match the list, and carry the original as context.

const MAX_STACK = 2000;

// body[data-controller] is the Rails controller path from body_tags: "reveal_bi" on
// the storefront, "backoffice_admin_reveal_bi" / "backoffice_analytics_reveal_bi" in
// the back office.
function revealSurface() {
  const path = document.body?.dataset?.controller || "";
  return path.startsWith("backoffice") ? "backoffice" : "storefront";
}

function describeCause(cause) {
  if (cause instanceof Error) {
    return { cause_name: cause.name, cause_message: cause.message, cause_stack: cause.stack?.slice(0, MAX_STACK) };
  }
  if (cause && typeof cause === "object") {
    return { cause_name: cause.constructor?.name || "Object", cause_message: String(cause.message ?? "") };
  }
  if (cause === undefined || cause === null) return {};
  return { cause_message: String(cause) };
}

// Adds no user or dashboard contents. Callers must sanitize untrusted response bodies
// before passing a cause; SDK error messages/stacks remain diagnostic free text.
export function captureRevealError(stage, cause, { dashboardId = "" } = {}) {
  const Sentry = window.Sentry;
  if (!Sentry?.captureException) return false;

  const surface = revealSurface();
  const error = new Error(`Reveal ${stage} failed`);
  const context = {
    tags: { "reveal.stage": stage, "reveal.surface": surface, "reveal.dashboard_id": dashboardId || "none" },
    contexts: { reveal: { stage, surface, dashboard_id: dashboardId || null, ...describeCause(cause) } },
    level: "error"
  };

  // captureException triggers the lazy loader and queues both arguments until after
  // the layout's onLoad initialization. Registering another onLoad callback alone
  // does not start loading, leaving the first swallowed failure invisible.
  Sentry.captureException(error, context);
  return true;
}

// Do not let fetch rejection details or response bodies reach console breadcrumbs.
export function sanitizeRevealSaveError(error) {
  const match = /^Save failed: (\d+)$/.exec(error?.message || "");
  return new Error(match ? `Save failed: ${match[1]}` : "Save failed");
}
